import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Mail, Phone, Send, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { reveal, revealContainer, viewportOnce } from "../../lib/motion";

const FIELDS = [
  { name: "name", label: "Name", type: "text", required: true, half: true },
  { name: "company", label: "Company", type: "text", half: true },
  { name: "email", label: "Email", type: "email", required: true, half: true },
  { name: "phone", label: "Phone", type: "tel", half: true },
  { name: "event_date", label: "Event Date", type: "date", half: true },
  { name: "guests", label: "Number of Guests", type: "number", half: true },
];

const EMPTY = { name: "", company: "", email: "", phone: "", event_date: "", guests: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent — we'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send enquiry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-charcoal py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          variants={revealContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="lg:col-span-5"
        >
          <motion.span variants={reveal} className="label-caps text-gold">
            Enquire
          </motion.span>
          <motion.h2
            variants={reveal}
            className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl text-cream leading-[1.05]"
          >
            Let&rsquo;s cater your <span className="italic text-gold">next event</span>
          </motion.h2>
          <motion.p variants={reveal} className="mt-6 text-cream/60 leading-relaxed max-w-md">
            Tell us about your occasion and we&rsquo;ll craft a menu that fits perfectly.
          </motion.p>

          <motion.div variants={reveal} className="mt-10 space-y-4">
            <a
              href="tel:+6402108010017"
              data-testid="contact-phone"
              className="flex items-center gap-4 text-cream/80 hover:text-gold transition-colors"
            >
              <Phone size={18} /> <span>0210 801 0017 (Miwa)</span>
            </a>
            <a
              href="mailto:okomecateringnz@gmail.com"
              data-testid="contact-email"
              className="flex items-center gap-4 text-cream/80 hover:text-gold transition-colors"
            >
              <Mail size={18} /> <span>okomecateringnz@gmail.com</span>
            </a>
            <a
              href="https://instagram.com/okomecatering.nz"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-instagram"
              className="flex items-center gap-4 text-cream/80 hover:text-gold transition-colors"
            >
              <Instagram size={18} /> <span>@okomecatering.nz</span>
            </a>
            <div className="flex items-start gap-4 text-cream/80">
              <MapPin size={18} className="mt-0.5" />
              <span>Edwin Street, Mount Eden, Auckland</span>
            </div>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {FIELDS.map((f) => (
              <div key={f.name} className={f.half ? "" : "sm:col-span-2"}>
                <label className="label-caps text-cream/50 block mb-2">{f.label}</label>
                <input
                  type={f.type}
                  name={f.name}
                  required={f.required}
                  value={form[f.name]}
                  onChange={onChange}
                  data-testid={`field-${f.name}`}
                  className="w-full bg-transparent border-b border-cream/20 py-3 text-cream placeholder-cream/30 focus:border-gold outline-none transition-colors"
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="label-caps text-cream/50 block mb-2">Message</label>
              <textarea
                name="message"
                rows={4}
                value={form.message}
                onChange={onChange}
                data-testid="field-message"
                className="w-full bg-transparent border-b border-cream/20 py-3 text-cream placeholder-cream/30 focus:border-gold outline-none transition-colors resize-none"
              />
            </div>
            <div className="sm:col-span-2 mt-2">
              <button
                type="submit"
                disabled={loading}
                data-testid="enquiry-submit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-gold px-10 py-4 text-charcoal font-medium hover:bg-cream transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Enquiry"}
                {!loading && <Send size={18} />}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
